// Custom fields pentru proiecte - actualizat
import { apiGet, apiPost, apiPatch, apiDelete, extractIdFromLink } from './api';
import type {
  Project,
  HalCollection,
  Member,
  Version,
  WorkPackageType,
  Category,
  User,
  ProjectMeta,
  ProjectNote,
  ProjectNoteVisibility,
} from '@/types';

// Proiectele active la care utilizatorul curent are acces:
//   admin -> toate; restul -> cele în care este membru (orice rol).
// NU filtrează după creator - de aceea numele vechi (getMyProjects / „created-by-me")
// era înșelător. Endpointul include și customFields.
export async function getAccessibleProjects(): Promise<Project[]> {
  const response = await apiGet<HalCollection<Project>>('/auth/data/projects/accessible');
  return response._embedded.elements;
}

// Obținere proiect după ID (folosește endpoint custom care include customFields)
export async function getProject(projectId: number): Promise<Project> {
  return apiGet<Project>(`/auth/data/projects/${projectId}`);
}

// Obținere proiect după identificator
export async function getProjectByIdentifier(identifier: string): Promise<Project> {
  return apiGet<Project>(`/projects/${identifier}`);
}

// Creare proiect nou
export async function createProject(data: {
  name: string;
  identifier?: string;
  description?: { raw: string };
  public?: boolean;
  parent?: string; // Link către proiect părinte
  status?: string;
  customField?: Record<string, unknown>;
}): Promise<Project> {
  const payload: Record<string, unknown> = {
    name: data.name,
  };

  if (data.identifier) {
    payload.identifier = data.identifier;
  }

  if (data.description) {
    payload.description = data.description;
  }

  if (data.public !== undefined) {
    payload.public = data.public;
  }

  // Links pentru relații
  const links: Record<string, unknown> = {};

  if (data.parent) {
    links.parent = { href: data.parent };
  }

  if (data.status) {
    links.status = { href: data.status };
  }

  if (Object.keys(links).length > 0) {
    payload._links = links;
  }

  // Câmpuri custom (include startDate, dueDate și toate câmpurile de contract)
  if (data.customField) {
    payload.customField = data.customField;
  }

  return apiPost<Project>('/auth/data/projects', payload);
}

// Actualizare proiect
export async function updateProject(
  projectId: number,
  data: {
    name?: string;
    description?: { raw: string };
    public?: boolean;
    parent?: string | null;
    status?: string;
    customField?: Record<string, unknown>;
  }
): Promise<Project> {
  const payload: Record<string, unknown> = {};

  if (data.name !== undefined) {
    payload.name = data.name;
  }

  if (data.description !== undefined) {
    payload.description = data.description;
  }

  if (data.public !== undefined) {
    payload.public = data.public;
  }

  // Links pentru relații
  const links: Record<string, unknown> = {};

  if (data.parent !== undefined) {
    links.parent = data.parent ? { href: data.parent } : null;
  }

  if (data.status !== undefined) {
    links.status = { href: data.status };
  }

  if (Object.keys(links).length > 0) {
    payload._links = links;
  }

  // Câmpuri custom (include startDate, dueDate și câmpurile de contract)
  if (data.customField) {
    payload.customField = data.customField;
  }

  return apiPatch<Project>(`/auth/data/projects/${projectId}`, payload);
}

// Ștergere proiect
export async function deleteProject(projectId: number): Promise<void> {
  await apiDelete(`/projects/${projectId}`);
}

// Obținere membri proiect (folosind endpoint direct DB pentru acces admin)
export async function getProjectMembers(projectId: number): Promise<Member[]> {
  try {
    // Încercăm mai întâi endpoint-ul direct DB (fără restricții de permisiuni)
    const response = await apiGet<HalCollection<Member>>(
      `/auth/data/projects/${projectId}/members`
    );
    return response._embedded?.elements || [];
  } catch {
    // Fallback la API-ul standard OpenProject
    const response = await apiGet<HalCollection<Member>>(
      `/memberships`,
      {
        params: {
          pageSize: 100,
          filters: JSON.stringify([
            { project: { operator: '=', values: [String(projectId)] } }
          ]),
        },
      }
    );
    return response._embedded?.elements || [];
  }
}

// Obținere versiuni proiect
export async function getProjectVersions(projectId: number): Promise<Version[]> {
  const response = await apiGet<HalCollection<Version>>(
    `/projects/${projectId}/versions`,
    {
      params: {
        pageSize: 100,
      },
    }
  );

  return response._embedded.elements;
}

// Obținere tipuri disponibile pentru proiect
export async function getProjectTypes(projectId: number): Promise<WorkPackageType[]> {
  const response = await apiGet<HalCollection<WorkPackageType>>(
    `/projects/${projectId}/types`
  );

  return response._embedded.elements;
}

// Obținere schema proiect (pentru câmpuri custom)
export async function getProjectSchema(): Promise<Record<string, unknown>> {
  return apiGet<Record<string, unknown>>('/projects/schema');
}

// Obținere form pentru creare proiect
export async function getProjectCreateForm(): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/projects/form', {});
}

// Verificare dacă utilizatorul poate crea proiecte
export async function canCreateProject(): Promise<boolean> {
  try {
    await getProjectCreateForm();
    return true;
  } catch {
    return false;
  }
}

// Helper pentru a extrage ID din project link
export function getProjectIdFromLink(project: Project): number {
  return project.id || extractIdFromLink(project._links.self) || 0;
}

// Helper pentru a obține proiectul părinte
export function getParentProjectId(project: Project): number | null {
  return extractIdFromLink(project._links.parent);
}

// Obținere categorii proiect
export async function getProjectCategories(projectId: number): Promise<Category[]> {
  try {
    const response = await apiGet<HalCollection<Category>>(
      `/auth/data/projects/${projectId}/categories`
    );
    return response._embedded?.elements || [];
  } catch {
    return [];
  }
}

// Obținere utilizatori disponibili pentru atribuire în proiect
export async function getProjectAssignees(projectId: number): Promise<User[]> {
  try {
    const response = await apiGet<HalCollection<User>>(
      `/auth/data/projects/${projectId}/available-assignees`
    );
    return response._embedded?.elements || [];
  } catch {
    return [];
  }
}

// Tip pentru rol
export interface ProjectRole {
  id: number;
  name: string;
  position?: number;
  _links?: {
    self: { href: string };
  };
}

// Obținere toate rolurile de proiect disponibile
export async function getProjectRoles(): Promise<ProjectRole[]> {
  try {
    const response = await apiGet<HalCollection<ProjectRole>>(
      '/auth/data/roles'
    );
    return response._embedded?.elements || [];
  } catch {
    return [];
  }
}

// Obținere utilizatori disponibili pentru a fi adăugați la un proiect
export async function getAvailableUsersForProject(projectId: number): Promise<User[]> {
  try {
    const response = await apiGet<HalCollection<User>>(
      '/auth/data/users/available',
      {
        params: {
          excludeProjectId: projectId,
        },
      }
    );
    return response._embedded?.elements || [];
  } catch {
    return [];
  }
}

// Adăugare membru la proiect
export async function addProjectMember(
  projectId: number,
  userId: number,
  roleIds: number[]
): Promise<Member> {
  return apiPost<Member>(
    `/auth/data/projects/${projectId}/members`,
    { userId, roleIds }
  );
}

// Modificare roluri membru existent
export async function updateProjectMemberRoles(
  projectId: number,
  memberId: number,
  roleIds: number[]
): Promise<Member> {
  return apiPatch<Member>(
    `/auth/data/projects/${projectId}/members/${memberId}/roles`,
    { roleIds }
  );
}

// Ștergere membru din proiect
export async function deleteProjectMember(
  projectId: number,
  memberId: number,
  reason?: string
): Promise<void> {
  return apiDelete(`/auth/data/projects/${projectId}/members/${memberId}`, { data: { reason } });
}

// Sursa (modulul) din care provine un fișier al proiectului
export type ProjectAttachmentSource =
  | 'openproject'
  | 'procurement'
  | 'contract'
  | 'deliverable'
  | 'implementation';

// Tip pentru atașament proiect
export interface ProjectAttachment {
  id: number;
  fileName: string;
  fileSize: number;
  contentType: string;
  digest?: string;
  downloads: number;
  createdAt: string;
  description?: string;
  // Vedere unificată: sursa fișierului (OpenProject nativ vs. modulele de dosar).
  // Backend-ul poate întoarce oricare dintre aceste câmpuri (opționale).
  source?: ProjectAttachmentSource | string;
  source_module?: ProjectAttachmentSource | string;
  // URL de descărcare direct furnizat de backend pentru documentele de dosar.
  downloadUrl?: string;
  _embedded?: {
    author?: {
      id: number;
      login: string;
      firstName?: string;
      lastName?: string;
      name: string;
    };
    container?: {
      id: number;
      subject: string;
      _type: string;
      workPackageType?: {
        id: number;
        name: string;
        isMilestone: boolean;
        color?: string;
      };
    };
  };
  _links: {
    self: { href: string };
    downloadLocation: { href: string };
    author?: { href: string };
    container?: { href: string; title?: string };
  };
}

// Obținere fișierele proiectului
export async function getProjectAttachments(projectId: number): Promise<ProjectAttachment[]> {
  try {
    const response = await apiGet<HalCollection<ProjectAttachment>>(
      `/auth/data/projects/${projectId}/attachments`
    );
    return response._embedded?.elements || [];
  } catch {
    return [];
  }
}

// ============== FAZA 2 — URGENT / URMĂREȘTE / NOTE ==============

// Obținere meta partajat pentru header (urgent, urmărire, număr note)
export async function getProjectMeta(projectId: number): Promise<ProjectMeta> {
  return apiGet<ProjectMeta>(`/auth/data/projects/${projectId}/meta`);
}

// Setare/anulare marcaj „Urgent" (partajat pe proiect)
export async function setProjectUrgent(
  projectId: number,
  value: boolean
): Promise<ProjectMeta> {
  return apiPost<ProjectMeta>(`/auth/data/projects/${projectId}/urgent`, { value });
}

// Abonare la modificările proiectului (urmărire)
export async function followProject(projectId: number): Promise<{ is_following: boolean }> {
  return apiPost<{ is_following: boolean }>(`/auth/data/projects/${projectId}/follow`, {});
}

// Dezabonare de la modificările proiectului
export async function unfollowProject(projectId: number): Promise<{ is_following: boolean }> {
  return apiDelete<{ is_following: boolean }>(`/auth/data/projects/${projectId}/follow`);
}

// Obținere note (personale ale userului + partajate ale proiectului)
export async function getProjectNotes(projectId: number): Promise<ProjectNote[]> {
  const response = await apiGet<{ notes: ProjectNote[] }>(
    `/auth/data/projects/${projectId}/notes`
  );
  return response.notes || [];
}

// Adăugare notă (personală sau partajată)
export async function addProjectNote(
  projectId: number,
  body: string,
  visibility: ProjectNoteVisibility
): Promise<ProjectNote> {
  return apiPost<ProjectNote>(`/auth/data/projects/${projectId}/notes`, {
    body,
    visibility,
  });
}

// Ștergere notă (doar autorul)
export async function deleteProjectNote(noteId: number): Promise<void> {
  await apiDelete(`/auth/data/projects/notes/${noteId}`);
}
